import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal, { Eyebrow } from "../../components/Reveal";
import { WHY_POINTS, PROCESS_STEPS, IMAGES } from "../../constants/content";
import { useSite } from "../../context/SiteContext";
export function Intro() {
  return (
    <section data-testid="intro-section" className="py-16 md:py-24 lg:py-28 bg-paper">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-5">
            <Eyebrow>Razor Build Limited</Eyebrow>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-5xl tracking-tight text-ink display-expanded leading-[1.02]">
              Your Wellington Building Partner
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7 lg:pt-10">
            <p className="text-lg md:text-xl text-ink/85 leading-relaxed font-medium">
              Razor Build provides professional residential construction throughout Wellington, Hutt Valley and Porirua.
            </p>
            <p className="mt-5 text-slate2 leading-relaxed max-w-2xl">
              We work with homeowners on renovations, alterations, extensions and new residential builds — from early discussions and plans through construction and completion. No jargon, no runaround: a straight conversation about what you want to achieve, practical building solutions, and the work done properly.
            </p>
            <p className="mt-5 text-slate2 leading-relaxed max-w-2xl">
              Whether you're planning a small improvement, renovating your home, adding more space or starting a new build, get in touch and tell us what you're planning — big or small, we're happy to have a chat.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function WhyUs() {
  return (
    <section data-testid="why-section" className="py-16 md:py-24 lg:py-28 bg-ink text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <Reveal>
          <Eyebrow light>Why Razor Build</Eyebrow>
          <h2 className="mt-4 font-display font-bold text-3xl md:text-5xl tracking-tight display-expanded leading-[1.02] max-w-2xl">
            Built the Way It Should Be.
          </h2>
        </Reveal>
        <div className="mt-14 md:mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {WHY_POINTS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.06} className="bg-ink">
              <div className="p-7 md:p-9 h-full" data-testid={`why-point-${i}`}>
                <span className="font-mono text-xs tracking-[0.25em] text-gold">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-4 font-display font-semibold text-xl leading-snug">{p.title}</h3>
                <p className="mt-3 text-sm text-white/60 leading-relaxed">{p.desc}</p>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.3} className="bg-ink">
            <Link to="/contact" data-testid="why-cta" className="flex flex-col justify-between h-full p-7 md:p-9 group bg-gold text-ink min-h-[180px]">
              <span className="font-display font-semibold text-xl leading-snug">Talk to us about your project</span>
              <span className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.18em] uppercase">
                Get In Touch <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function Process() {
  return (
    <section id="process" data-testid="process-section" className="py-16 md:py-24 lg:py-28 bg-white scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <Reveal>
          <Eyebrow>Our Process</Eyebrow>
          <h2 className="mt-4 font-display font-bold text-3xl md:text-5xl tracking-tight text-ink display-expanded leading-[1.02]">
            From First Conversation<br />to Handover.
          </h2>
        </Reveal>
        <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
          {PROCESS_STEPS.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.07}>
              <div data-testid={`process-step-${i}`} className="relative pl-8 border-l-2 border-line hover:border-gold transition-colors duration-300 py-1">
                <span className="absolute -left-[2px] top-1 w-[2px] h-8 bg-gold" />
                <span className="font-mono text-sm tracking-[0.2em] text-golddark">{s.num}</span>
                <h3 className="mt-2.5 font-display font-semibold text-xl text-ink">{s.title}</h3>
                <p className="mt-2.5 text-sm text-slate2 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function AboutTeaser() {
  const { images } = useSite();
  return (
    <section data-testid="about-teaser-section" className="py-16 md:py-24 lg:py-28 bg-paper">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <Reveal className="lg:col-span-6">
            <div className="relative">
              <div className="w-full aspect-[4/5] md:aspect-[4/4] bg-white border border-line flex items-center justify-center overflow-hidden">
                <img src={images.consult} alt="Razor Build builder talking through a project with a Wellington homeowner" loading="lazy" className="w-full h-full object-contain" />
              </div>
              <div className="absolute -bottom-6 -right-4 md:-right-6 bg-ink text-white px-6 py-5 md:px-8 md:py-6">
                <p className="font-display font-bold text-3xl md:text-4xl text-gold">10+</p>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/70 mt-1">Years Experience</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-6">
            <Eyebrow>About Razor Build</Eyebrow>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-5xl tracking-tight text-ink display-expanded leading-[1.02]">
              Built on Experience.<br />Focused on Quality.
            </h2>
            <p className="mt-6 text-slate2 leading-relaxed">
              Good building isn't only about how the finished project looks. It also comes down to planning, workmanship, communication and getting the details right throughout construction.
            </p>
            <p className="mt-4 font-mono text-xs tracking-[0.25em] uppercase text-golddark">Build • Renovate • Maintain</p>
            <Link to="/about" data-testid="about-teaser-link" className="mt-8 inline-flex items-center gap-2.5 border border-ink px-6 py-3.5 font-mono text-[11px] tracking-[0.18em] uppercase text-ink hover:bg-ink hover:text-white transition-colors">
              More About Us <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function ServiceAreas() {
  const site = useSite();
  return (
    <section data-testid="service-areas-section" className="py-16 md:py-24 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <Reveal>
          <Eyebrow>Service Area</Eyebrow>
          <h2 className="mt-4 font-display font-bold text-3xl md:text-5xl tracking-tight text-ink display-expanded leading-[1.02]">
            Building Across the<br />Wellington Region
          </h2>
        </Reveal>
        <div className="mt-12 md:mt-16 grid grid-cols-2 md:grid-cols-3 gap-px bg-line border border-line">
          {site.service_areas.map((area, i) => (
            <Reveal key={area} delay={i * 0.05}>
              <div data-testid={`service-area-${i}`} className="bg-paper hover:bg-white transition-colors px-6 py-8 md:py-10 h-full flex flex-col justify-between gap-6 group">
                <span className="font-mono text-[10px] tracking-[0.25em] text-slate2/60">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-display font-semibold text-lg md:text-xl text-ink group-hover:text-golddark transition-colors">{area}</span>
              </div>
            </Reveal>
          ))}
          <Reveal delay={site.service_areas.length * 0.05}>
            <Link to="/contact" data-testid="service-area-other" className="bg-ink px-6 py-8 md:py-10 h-full flex flex-col justify-between gap-6 group">
              <span className="font-mono text-[10px] tracking-[0.25em] text-white/40">elsewhere?</span>
              <span className="font-display font-semibold text-lg md:text-xl text-white group-hover:text-gold transition-colors">Your Area — Ask Us</span>
            </Link>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <p className="mt-8 text-sm text-slate2 max-w-xl leading-relaxed">
            Based in the Wellington region and working across Wellington City, Lower Hutt, Upper Hutt and Porirua. If your project is a little further out, get in touch — we'll let you know honestly whether we can take it on.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
